import { useState, useEffect, useRef } from 'react';
import {
  Palette, Save, Loader2, AlertCircle, CheckCircle,
  Monitor, Layers, RotateCw, Settings, Eye, Edit3, X, Plus, Minus
} from 'lucide-react';
import { Switch, ColorPickerPanel, MessagePlugin } from 'tdesign-react';
import ProductImageUploader from '@/user/components/ProductImageUploader';
import { cardTemplateApi, tokenStorage } from '@/user/api';
import type { CardTemplateVO, GradientColorConfig, GradientMode } from '@/types/eCard';
import { PreviewableImage } from './components/ImagePreview';
import { getOrgLabel } from './constants';

const presetColors = [
  '#1DA9FF', '#52c41a', '#faad14', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#2f54eb', '#1890ff', '#a0d911',
];

const FALLBACK_COLOR = '#1DA9FF';
const MAX_GRADIENT_STOPS = 5;

const GRADIENT_MODE_META: { value: GradientMode; label: string; hint: string }[] = [
  { value: 'solid', label: '纯色', hint: '使用单一颜色' },
  { value: 'linear', label: '线性渐变', hint: '按指定方向渐变过渡' },
  { value: 'radial', label: '径向渐变', hint: '由中心向外辐射渐变' },
];

const DIRECTION_OPTIONS = [
  { angle: 0, label: '向右' },
  { angle: 45, label: '右下' },
  { angle: 90, label: '向下' },
  { angle: 135, label: '左下' },
  { angle: 180, label: '向左' },
  { angle: 225, label: '左上' },
  { angle: 270, label: '向上' },
  { angle: 315, label: '右上' },
];

function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

/** 将渐变配置转换为可直接使用的 CSS background 值（仅取合法色值） */
function gradientCss(g?: GradientColorConfig | null): string {
  const colors = (g?.colors || []).filter((c) => isValidHex(String(c || '').trim()));
  const list = colors.length ? colors : [FALLBACK_COLOR];
  if (!g || g.mode === 'solid' || list.length < 2) return list[0];
  if (g.mode === 'radial') return `radial-gradient(circle at 50% 50%, ${list.join(', ')})`;
  const angle = Number.isFinite(g.angle) ? Math.round(g.angle) : 135;
  return `linear-gradient(${angle}deg, ${list.join(', ')})`;
}

/** 加深(amount<0) / 变浅(amount>0) 十六进制颜色 */
function shiftHex(hex: string, amount: number): string {
  let h = String(hex || FALLBACK_COLOR).replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return FALLBACK_COLOR;
  const num = parseInt(h, 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;
  const target = amount < 0 ? 0 : 255;
  const p = Math.min(100, Math.abs(amount)) / 100;
  r = Math.round((target - r) * p + r);
  g = Math.round((target - g) * p + g);
  b = Math.round((target - b) * p + b);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function solidGradient(color: string): GradientColorConfig {
  return { mode: 'solid', colors: [isValidHex(color) ? color : FALLBACK_COLOR], angle: 135 };
}

// ============ 渐变色编辑器（主题色 / 按钮颜色通用） ============
function GradientColorField({
  value,
  onChange,
  label,
  description,
}: {
  value: GradientColorConfig;
  onChange: (v: GradientColorConfig) => void;
  label: string;
  description?: string;
}) {
  const lastValidRef = useRef<Record<number, string>>({});
  const colors = value?.colors?.length ? value.colors : [FALLBACK_COLOR];
  const isSolid = value.mode === 'solid';
  const css = gradientCss(value);
  const angle = Number.isFinite(value.angle) ? Math.round(value.angle) : 135;

  const changeMode = (mode: GradientMode) => {
    let next = colors.map((c) => (isValidHex(c) ? c : FALLBACK_COLOR));
    if (mode === 'solid') {
      next = [next[0] || FALLBACK_COLOR];
    } else if (next.length === 0) {
      next = [FALLBACK_COLOR, shiftHex(FALLBACK_COLOR, -20)];
    } else if (next.length === 1) {
      next = [next[0], shiftHex(next[0], -20)];
    }
    onChange({ mode, colors: next, angle });
  };

  const updateColor = (index: number, color: string) => {
    if (isValidHex(color)) lastValidRef.current[index] = color;
    const next = [...colors];
    next[index] = color;
    onChange({ ...value, colors: next });
  };

  const blurColor = (index: number) => {
    if (isValidHex(colors[index])) return;
    updateColor(index, lastValidRef.current[index] || FALLBACK_COLOR);
  };

  const addColor = () => {
    if (colors.length >= MAX_GRADIENT_STOPS) return;
    const base = colors[colors.length - 1] || FALLBACK_COLOR;
    onChange({ ...value, colors: [...colors, shiftHex(base, 14)] });
  };

  const removeColor = (index: number) => {
    const next = colors.filter((_, i) => i !== index);
    if (!next.length) next.push(FALLBACK_COLOR);
    onChange({ ...value, colors: next });
  };

  const canRemove = isSolid ? colors.length > 1 : colors.length > 2;
  const canAdd = !isSolid && colors.length < MAX_GRADIENT_STOPS;

  return (
    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
      {/* 标题与当前效果 */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <label className="block text-sm font-semibold text-gray-800">{label}</label>
          {description && <span className="text-xs text-gray-400">{description}</span>}
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border border-gray-200 shrink-0 max-w-[46%]">
          <span
            className="w-3.5 h-3.5 rounded border border-gray-200 shrink-0"
            style={{ background: css }}
          />
          <span className="text-[11px] font-mono text-gray-500 truncate" title={css}>{css}</span>
        </div>
      </div>

      {/* 渐变方式 */}
      <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-gray-200 w-fit mb-4">
        {GRADIENT_MODE_META.map((m) => (
          <button
            key={m.value}
            type="button"
            title={m.hint}
            onClick={() => changeMode(m.value)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              value.mode === m.value
                ? 'bg-[#1DA9FF] text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* 渐变色标颜色 */}
      <div className="space-y-2">
        {colors.map((color, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="w-14 text-xs text-gray-400 shrink-0">{isSolid ? '颜色' : `色标 ${index + 1}`}</span>
            <input
              type="color"
              value={isValidHex(color) ? color : FALLBACK_COLOR}
              onChange={(e) => updateColor(index, e.target.value)}
              className="w-8 h-8 rounded-md border border-gray-200 cursor-pointer bg-white p-0.5 shrink-0"
              title="点击打开取色器"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                const raw = e.target.value;
                const cleaned = raw.startsWith('#') ? raw : raw ? `#${raw}` : '';
                if (/^#([0-9a-fA-F]{0,6})$/.test(cleaned)) updateColor(index, cleaned);
              }}
              onBlur={() => blurColor(index)}
              className="flex-1 h-8 px-2 rounded-md border border-gray-200 bg-white text-xs font-mono focus:border-[#1DA9FF] focus:ring-2 focus:ring-[#1DA9FF]/20 outline-none transition-all min-w-0"
              placeholder="#1DA9FF"
            />
            {canRemove && (
              <button
                type="button"
                onClick={() => removeColor(index)}
                title="删除该色标"
                className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* 添加渐变色 */}
      {canAdd && (
        <button
          type="button"
          onClick={addColor}
          className="mt-2 flex items-center gap-1 text-xs text-[#1DA9FF] hover:text-[#0b86d9] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          添加渐变色标（最多 {MAX_GRADIENT_STOPS} 个）
        </button>
      )}

      {/* 线性渐变方向 */}
      {value.mode === 'linear' && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">渐变方向</span>
            <span className="text-xs font-mono text-gray-400">{angle}°</span>
          </div>
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {DIRECTION_OPTIONS.map((d) => (
              <button
                key={d.angle}
                type="button"
                onClick={() => onChange({ ...value, angle: d.angle })}
                className={`px-2 py-1 rounded-md border text-[11px] transition-all ${
                  angle === d.angle
                    ? 'border-[#1DA9FF] bg-blue-50 text-[#1DA9FF] font-medium'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={360}
              step={5}
              value={angle}
              onChange={(e) => onChange({ ...value, angle: Number(e.target.value) })}
              className="flex-1 accent-[#1DA9FF]"
            />
            <span className="text-sm font-medium text-gray-700 w-12 text-right">{angle}°</span>
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>0°（向右）</span>
            <span>90°（向下）</span>
            <span>180°（向左）</span>
            <span>270°（向上）</span>
          </div>
        </div>
      )}

      {/* 实时预览 */}
      <div className="mt-3 flex items-center gap-2">
        <span className="w-14 text-xs text-gray-400 shrink-0">预览</span>
        <div
          className="h-8 flex-1 rounded-md border border-gray-200 shadow-inner"
          style={{ background: css }}
        />
      </div>
    </div>
  );
}

// ============ 渐变色只读展示（主题色 / 按钮颜色通用） ============
function GradientColorDisplay({
  label,
  gradient,
  description,
}: {
  label: string;
  gradient?: GradientColorConfig | null;
  description?: string;
}) {
  const css = gradientCss(gradient);
  const mode = gradient?.mode || 'solid';
  const modeMeta = GRADIENT_MODE_META.find((m) => m.value === mode);
  const colorCount = (gradient?.colors || []).filter(Boolean).length;
  return (
    <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm shrink-0"
          style={{ background: css }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-500 whitespace-nowrap">
              {modeMeta ? modeMeta.label : mode}
              {mode === 'linear' ? ` · ${Math.round(gradient?.angle ?? 135)}°` : ''}
              {colorCount ? ` · ${colorCount}色` : ''}
            </span>
          </div>
          <div className="text-xs font-mono text-gray-500 truncate mt-0.5" title={css}>{css}</div>
          {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
        </div>
      </div>
    </div>
  );
}

/** 从 CSS 颜色/渐变字符串中提取第一个合法 hex（用于纯色兜底字段） */
function firstHexColor(css?: string | null): string {
  const matches = String(css || '').match(/#[0-9a-fA-F]{6}/g);
  return matches && matches.length ? matches[0] : '';
}

/** 将后端返回的 CSS 渐变/纯色字符串解析回 GradientColorConfig（用于编辑态回显） */
function parseGradientCss(css?: string | null, fallback?: GradientColorConfig): GradientColorConfig {
  const s = String(css || '').trim();
  const fb = fallback || solidGradient(FALLBACK_COLOR);
  if (!s) return fb;
  if (/radial-gradient\(/i.test(s)) {
    const inner = s.replace(/^radial-gradient\(/i, '').replace(/\)\s*$/, '');
    const colors = inner.split(',').map((t) => t.trim()).filter((t) => isValidHex(t));
    return { mode: 'radial', colors: colors.length ? colors : [FALLBACK_COLOR], angle: 135 };
  }
  if (/linear-gradient\(/i.test(s)) {
    const inner = s.replace(/^linear-gradient\(/i, '').replace(/\)\s*$/, '');
    const parts = inner.split(',').map((t) => t.trim()).filter(Boolean);
    let angle = 135;
    const colors: string[] = [];
    parts.forEach((p, i) => {
      const deg = i === 0 ? p.match(/^(-?\d+(?:\.\d+)?)deg$/i) : null;
      if (deg) angle = Math.round(Number(deg[1]));
      else if (isValidHex(p)) colors.push(p);
    });
    return { mode: 'linear', colors: colors.length ? colors : [FALLBACK_COLOR], angle };
  }
  if (isValidHex(s)) return { mode: 'solid', colors: [s], angle: 135 };
  const first = firstHexColor(s);
  return { mode: 'solid', colors: [first || FALLBACK_COLOR], angle: 135 };
}

/** 兼容对象/字符串两种历史数据，统一转成 CSS 字符串 */
function gradientValueToCss(v?: GradientColorConfig | string | null): string {
  if (!v) return '';
  return typeof v === 'string' ? v : gradientCss(v);
}

/** 编辑态本地配色（渐变用对象便于编辑，提交时转为字符串） */
interface LocalColorConfig {
  themeColor: string;
  backgroundColor: string;
  buttonColor: string;
  dividerColor: string;
  textHighlightColor: string;
  textNormalColor: string;
  backgroundGradient: GradientColorConfig;
  buttonGradient: GradientColorConfig;
}
type LocalTemplate = Omit<CardTemplateVO, 'colorConfig'> & { colorConfig: LocalColorConfig };

/** 后端 colorConfig（字符串/旧对象兼容）→ 编辑态本地配色 */
function normalizeColorConfig(cc: any): LocalColorConfig {
  const src: any = cc || {};
  const legacyThemeCss = gradientValueToCss(src.themeGradient);
  const bgCss = gradientValueToCss(src.backgroundGradient) || legacyThemeCss;
  const buttonCss = gradientValueToCss(src.buttonColor) || gradientValueToCss(src.buttonGradient);
  return {
    themeColor: firstHexColor(src.themeColor) || FALLBACK_COLOR,
    backgroundColor: firstHexColor(src.backgroundColor) || firstHexColor(src.themeColor) || FALLBACK_COLOR,
    buttonColor: firstHexColor(buttonCss) || FALLBACK_COLOR,
    dividerColor: firstHexColor(src.dividerColor) || '#eeeeee',
    textHighlightColor: firstHexColor(src.textHighlightColor) || '#000000',
    textNormalColor: firstHexColor(src.textNormalColor) || '#666666',
    backgroundGradient: parseGradientCss(bgCss),
    buttonGradient: parseGradientCss(buttonCss),
  };
}

const defaultTemplate: LocalTemplate = {
  templateName: '',
  logo: '',
  status: 1,
  tenantId: 0,
  colorConfig: {
    buttonColor: '#1DA9FF',
    dividerColor: '#eeeeee',
    textHighlightColor: '#000000',
    textNormalColor: '#666666',
    themeColor: '#1DA9FF',
    backgroundColor: '#1DA9FF',
    backgroundGradient: solidGradient('#1DA9FF'),
    buttonGradient: solidGradient('#1DA9FF'),
  },
  navBarConfig: {
    backgroundColor: '#ffffff',
    textColor: '#333333',
  },
  tabbarConfig: {
    highlightColor: '#1DA9FF',
    normalColor: '#999999',
  },
  watermarkConfig: {
    angle: -15,
    textColor: '#ff0000',
  },
  personalCenterBg: '',
};

function ColorPickerField({
  value,
  onChange,
  label,
  description
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
      <div className="flex items-center justify-between mb-3">
        <div>
          <label className="block text-sm font-semibold text-gray-800">{label}</label>
          {description && <span className="text-xs text-gray-400">{description}</span>}
        </div>
        {value && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border border-gray-200">
            <div className="w-3 h-3 rounded border border-gray-200" style={{ backgroundColor: value }} />
            <span className="text-xs font-mono font-medium text-gray-600">{value.toUpperCase()}</span>
          </div>
        )}
      </div>
      <ColorPickerPanel
        value={value}
        onChange={(val: string) => onChange(val)}
        format="HEX"
        clearable
        borderless
        swatchColors={presetColors}
        showPrimaryColorPreview
      />
    </div>
  );
}

function ColorDisplay({ label, value, description }: { label: string; value: string; description?: string }) {
  return (
    <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm shrink-0"
          style={{ backgroundColor: value }}
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-700">{label}</div>
          <div className="text-xs font-mono text-gray-500">{value?.toUpperCase()}</div>
          {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
        </div>
      </div>
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium">{value}</span>
    </div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1DA9FF]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function ThemeConfiguration() {
  const [template, setTemplate] = useState<LocalTemplate>(defaultTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);

  // 机构称谓（企业/单位），该页面企业与政务单位共用
  const tenantInfo = tokenStorage.getTenantInfo();
  const orgLabel = getOrgLabel(tenantInfo?.orgType, tenantInfo?.tenantType);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await cardTemplateApi.getTemplate();
      if (res.code === 200 && res.data) {
        setTemplate({
          ...defaultTemplate,
          ...res.data,
          colorConfig: normalizeColorConfig(res.data.colorConfig),
          navBarConfig: { ...defaultTemplate.navBarConfig, ...res.data.navBarConfig },
          tabbarConfig: { ...defaultTemplate.tabbarConfig, ...res.data.tabbarConfig },
          watermarkConfig: { ...defaultTemplate.watermarkConfig, ...res.data.watermarkConfig },
        });
        setHasData(true);
        setIsEditing(false);
      } else {
        setHasData(false);
        setIsEditing(true);
      }
    } catch {
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.templateName.trim()) {
      setError('请输入模板名称');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const tenantInfo = tokenStorage.getTenantInfo();
      // 后端约定：buttonColor / backgroundGradient 以字符串（CSS 值）传递
      const submitData: CardTemplateVO = {
        ...template,
        tenantId: tenantInfo?.id || 0,
        colorConfig: {
          themeColor: template.colorConfig.themeColor,
          buttonColor: gradientCss(template.colorConfig.buttonGradient),
          dividerColor: template.colorConfig.dividerColor,
          textHighlightColor: template.colorConfig.textHighlightColor,
          textNormalColor: template.colorConfig.textNormalColor,
          backgroundGradient: gradientCss(template.colorConfig.backgroundGradient),
        },
      };
      const res = template.id
        ? await cardTemplateApi.saveTemplate(submitData)
        : await cardTemplateApi.saveTemplate(submitData);
      if (res.code === 200 && res.data) {
        // 与 loadData 一致：合并默认值，防止后端返回缺少字段导致渲染崩溃
        setTemplate({
          ...defaultTemplate,
          ...res.data,
          colorConfig: normalizeColorConfig(res.data.colorConfig),
          navBarConfig: { ...defaultTemplate.navBarConfig, ...res.data.navBarConfig },
          tabbarConfig: { ...defaultTemplate.tabbarConfig, ...res.data.tabbarConfig },
          watermarkConfig: { ...defaultTemplate.watermarkConfig, ...res.data.watermarkConfig },
        });
        setHasData(true);
        setIsEditing(false);
        setMessage(template.id ? '更新成功' : '保存成功');
        setTimeout(() => setMessage(''), 3000);
        MessagePlugin.success(template.id ? '更新成功' : '保存成功');
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
    } else {
      loadData();
      setIsEditing(false);
    }
  };

  const updateTemplate = (path: string, value: any) => {
    setTemplate((prev: any) => {
      const next: any = { ...prev };
      const keys = path.split('.');
      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  };

  /** 更新按钮 / 主题背景色的渐变配置，并把首色同步为纯色兜底主色（兼容只读旧字段） */
  const updateGradientColor = (kind: 'button' | 'background', gradient: GradientColorConfig) => {
    const primary = gradient.colors?.[0] || FALLBACK_COLOR;
    setTemplate((prev) => ({
      ...prev,
      colorConfig: {
        ...prev.colorConfig,
        ...(kind === 'button'
          ? { buttonColor: primary, buttonGradient: gradient }
          : { backgroundColor: primary, backgroundGradient: gradient }),
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1DA9FF] animate-spin" />
      </div>
    );
  }

  const showEditActions = isEditing;
  const showReadOnlyActions = !isEditing && hasData;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-6 h-6 text-[#1DA9FF]" />
          主题配置
        </h1>
        {showReadOnlyActions && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-[#1DA9FF] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
          >
            <Edit3 className="w-4 h-4" />
            编辑
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {message}
        </div>
      )}

      <div className="space-y-4">
        {/* 基础信息 */}
        <SectionCard title="基础信息" icon={Settings}>
          {!isEditing ? (
            <div className="divide-y divide-gray-50">
              <DisplayRow label="模板名称" value={template.templateName || '-'} />
              <DisplayRow label={`${orgLabel}Logo`} value={template.logo ? (
                <PreviewableImage src={template.logo} alt="logo" className="h-12 rounded" />
              ) : '-'} />
              <DisplayRow label="状态" value={
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${template.status === 1 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                  {template.status === 1 ? '已启用' : '已停用'}
                </span>
              } />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">模板名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={template.templateName}
                  onChange={(e) => updateTemplate('templateName', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1DA9FF] focus:ring-2 focus:ring-[#1DA9FF]/20 outline-none transition-all"
                  placeholder="如：默认蓝色主题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{orgLabel}Logo</label>
                <ProductImageUploader
                  value={template.logo}
                  onChange={(val) => updateTemplate('logo', val)}
                  simple
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
                <div className="flex items-center gap-4">
                  <Switch
                    value={template.status === 1}
                    onChange={(v) => updateTemplate('status', v ? 1 : 0)}
                  />
                  <span className="text-sm text-gray-600">
                    {template.status === 1 ? '已启用' : '已停用'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* 颜色配置 */}
        <SectionCard title="颜色配置" icon={Palette}>
          {!isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorDisplay label="主题色" value={template.colorConfig.themeColor} description="主要强调色（纯色），用于标题、图标等" />
                <GradientColorDisplay label="主题背景色" gradient={template.colorConfig.backgroundGradient} description="主题背景色，支持纯色/渐变" />
                <GradientColorDisplay label="按钮颜色" gradient={template.colorConfig.buttonGradient} description="按钮背景色，支持纯色/渐变" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorDisplay label="文字与名片标题色" value={template.colorConfig.textHighlightColor} description="强调文字颜色" />
                <ColorDisplay label="文字常规色" value={template.colorConfig.textNormalColor} description="普通文字颜色" />
                <ColorDisplay label="分割线颜色" value={template.colorConfig.dividerColor} description="线条和边框颜色" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorPickerField
                  label="主题色"
                  description="主要强调色（纯色），用于标题、图标等强调元素"
                  value={template.colorConfig.themeColor}
                  onChange={(v) => updateTemplate('colorConfig.themeColor', v)}
                />
                <GradientColorField
                  label="主题背景色"
                  description="主题背景色：可选纯色 / 线性 / 径向渐变，并自定义渐变方向"
                  value={template.colorConfig.backgroundGradient}
                  onChange={(g) => updateGradientColor('background', g)}
                />
                <GradientColorField
                  label="按钮颜色"
                  description="按钮背景色：可选纯色 / 线性 / 径向渐变，并自定义渐变方向"
                  value={template.colorConfig.buttonGradient}
                  onChange={(g) => updateGradientColor('button', g)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ColorPickerField label="文字与名片标题色" description="强调文字颜色" value={template.colorConfig.textHighlightColor} onChange={(v) => updateTemplate('colorConfig.textHighlightColor', v)} />
                <ColorPickerField label="文字常规色" description="普通文字颜色" value={template.colorConfig.textNormalColor} onChange={(v) => updateTemplate('colorConfig.textNormalColor', v)} />
                <ColorPickerField label="分割线颜色" description="线条和边框颜色" value={template.colorConfig.dividerColor} onChange={(v) => updateTemplate('colorConfig.dividerColor', v)} />
              </div>
            </div>
          )}
        </SectionCard>

        {/* 导航栏配置 */}
        {/* <SectionCard title="导航栏配置" icon={Monitor}>
          {!isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ColorDisplay label="导航栏背景色" value={template.navBarConfig.backgroundColor} description="顶部导航背景" />
              <ColorDisplay label="导航栏文字色" value={template.navBarConfig.textColor} description="导航文字颜色" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorPickerField label="导航栏背景色" description="顶部导航背景" value={template.navBarConfig.backgroundColor} onChange={(v) => updateTemplate('navBarConfig.backgroundColor', v)} />
              <ColorPickerField label="导航栏文字色" description="导航文字颜色" value={template.navBarConfig.textColor} onChange={(v) => updateTemplate('navBarConfig.textColor', v)} />
            </div>
          )}
        </SectionCard> */}

        {/* 底部Tabbar */}
        <SectionCard title="tabbar与个人背景图（宽度需要适配微信小程序，高度不限）" icon={Layers}>
          {!isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorDisplay label="普通颜色" value={template.tabbarConfig.normalColor} description="未选中状态" />
                <ColorDisplay label="高亮颜色" value={template.tabbarConfig.highlightColor} description="选中状态" />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-gray-50 pt-4">
                <span className="text-sm text-gray-500">个人中心背景图</span>
                {template.personalCenterBg ? (
                  <PreviewableImage src={template.personalCenterBg} alt="bg" className="w-16 h-10 rounded object-cover" />
                ) : (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorPickerField label="普通颜色" description="未选中状态" value={template.tabbarConfig.normalColor} onChange={(v) => updateTemplate('tabbarConfig.normalColor', v)} />
                <ColorPickerField label="高亮颜色" description="选中状态" value={template.tabbarConfig.highlightColor} onChange={(v) => updateTemplate('tabbarConfig.highlightColor', v)} />
              </div> 
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">个人中心背景图</label>
                <ProductImageUploader
                  value={template.personalCenterBg}
                  onChange={(val) => updateTemplate('personalCenterBg', val)}
                  simple
                />
              </div>
            </div>
          )}
        </SectionCard>

        {/* 水印配置 */}
        <SectionCard title="水印配置" icon={RotateCw}>
          {!isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ColorDisplay label="水印文字颜色" value={template.watermarkConfig.textColor} description={`水印显示颜色，倾斜角度为${template.watermarkConfig.angle}°`} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50 space-y-2">
                <label className="block text-sm font-semibold text-gray-800">倾斜角度</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="-45"
                    max="45"
                    value={template.watermarkConfig.angle}
                    onChange={(e) => updateTemplate('watermarkConfig.angle', Number(e.target.value))}
                    className="flex-1 accent-[#1DA9FF]"
                  />
                  <span className="text-sm font-medium text-gray-700 w-12 text-right">{template.watermarkConfig.angle}°</span>
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>-45°</span>
                  <span>0°</span>
                  <span>45°</span>
                </div>
              </div>
              <ColorPickerField label="水印文字颜色" description="水印显示颜色" value={template.watermarkConfig.textColor} onChange={(v) => updateTemplate('watermarkConfig.textColor', v)} />
            </div>
          )}
        </SectionCard>

        {/* 效果预览 */}
        {/* <SectionCard title="效果预览" icon={Eye}>
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: template.navBarConfig.backgroundColor, color: template.navBarConfig.textColor }}
            >
              <span className="font-bold">{template.templateName || '模板名称'}</span>
              <span className="text-sm">导航栏预览</span>
            </div>
            <div className="p-4 space-y-3 bg-white">
              <div className="flex items-center gap-3">
                {template.logo && (
                  <img src={template.logo} alt="logo" className="w-10 h-10 rounded object-cover" />
                )}
                <span className="font-medium" style={{ color: template.colorConfig.textNormalColor }}>
                  {template.templateName || `${orgLabel}名称`}
                </span>
              </div>
              <div className="h-px" style={{ backgroundColor: template.colorConfig.dividerColor }} />
              <div className="space-y-2">
                <div className="text-sm" style={{ color: template.colorConfig.textNormalColor }}>
                  文字常规色效果展示
                </div>
                <div className="text-sm font-bold" style={{ color: template.colorConfig.textHighlightColor }}>
                  文字高亮色效果展示
                </div>
              </div>
              <button
                className="w-full py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: template.colorConfig.buttonColor }}
              >
                按钮预览
              </button>
            </div>
            <div
              className="px-4 py-3 flex items-center justify-around"
              style={{ borderTop: `1px solid ${template.colorConfig.dividerColor}` }}
            >
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded bg-gray-200" />
                <span className="text-xs" style={{ color: template.tabbarConfig.normalColor }}>首页</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded bg-gray-200" />
                <span className="text-xs" style={{ color: template.tabbarConfig.normalColor }}>发现</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <div className="w-5 h-5 rounded" style={{ backgroundColor: template.tabbarConfig.highlightColor }} />
                <span className="text-xs font-medium" style={{ color: template.tabbarConfig.highlightColor }}>我的</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">主题色</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: template.colorConfig.themeColor }} />
                <span className="text-gray-700">{template.colorConfig.themeColor}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">按钮颜色</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: template.colorConfig.buttonColor }} />
                <span className="text-gray-700">{template.colorConfig.buttonColor}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">水印角度</div>
              <div className="text-gray-700">{template.watermarkConfig.angle}°</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-gray-500 mb-1">状态</div>
              <div className="text-gray-700">{template.status === 1 ? '启用' : '停用'}</div>
            </div>
          </div>
        </SectionCard> */}

        {/* 底部操作区 */}
        {showEditActions && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-[#1DA9FF] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {template.id ? '更新模板' : '保存模板'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
